import { useEffect, useRef, useState } from 'react';

import { drawScale } from './drawScale';
import Styles from './ScaleNotes.module.css';

let instanceCounter = 0;

// Room under the staff for the fingering of missed notes (up to 4 stacked numbers).
const FINGERING_BAND = 104;

const readColor = (element, name, fallback) => {
  if (!element) return fallback;
  const value = getComputedStyle(element).getPropertyValue(name).trim();
  return value || fallback;
};

/**
 * One long staff for the whole scale with a bar over the note the player is
 * on. Notes the player missed turn red and get their correct fingering printed
 * underneath. The staff is redrawn only when the scale, instrument, tempo,
 * width or a note's result changes; stepping the bar to the next note does not
 * redraw anything.
 */
const ScaleNotes = ({
  notes,
  instrument,
  bpm,
  activeIndex,
  statuses = [],
  fingerings = [],
  stepMs = 90,
}) => {
  const [elementId] = useState(() => {
    instanceCounter += 1;
    return `scale-staff-${instanceCounter}`;
  });
  const wrapRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(0);
  const [layout, setLayout] = useState(null);
  const notesKey = notes.join(',');
  const statusKey = statuses
    .map((status) => (status === 'wrong' ? 'w' : status === 'right' ? 'r' : '-'))
    .join('');

  // Width comes from a window resize listener (frame-debounced) rather than a
  // ResizeObserver, which caused loop errors earlier in this project.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    let frame = null;
    const measure = () => {
      frame = null;
      setBoxWidth(Math.floor(wrap.clientWidth));
    };
    measure();
    const onResize = () => {
      if (frame === null) frame = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!boxWidth) return;
    setLayout(
      drawScale({
        elementId,
        notes,
        instrument,
        bpm,
        boxWidth,
        color: readColor(wrapRef.current, '--hm-ink', '#2a2118'),
        wrongColor: '#c8321e',
        rightColor: readColor(wrapRef.current, '--hm-sage', '#2e8b57'),
        noteStates: statuses,
      }),
    );
    // notesKey and statusKey stand in for the notes and statuses arrays, whose
    // identity changes between renders without their contents changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementId, notesKey, instrument, bpm, boxWidth, statusKey]);

  const x =
    layout && layout.noteXs.length > 0
      ? layout.noteXs[Math.min(activeIndex, layout.noteXs.length - 1)]
      : null;

  return (
    <div
      ref={wrapRef}
      className={Styles.wrap}
      style={{ height: (layout ? layout.pxHeight : 200) + FINGERING_BAND }}
    >
      <div id={elementId} className={Styles.staff} />

      {Number.isFinite(x) && (
        <div
          className={Styles.playhead}
          style={{
            transform: `translateX(${Math.max(0, Math.min(layout.pxWidth - 10, x - 5))}px)`,
            height: layout.pxHeight - 16,
            transitionDuration: `${stepMs}ms`,
          }}
        />
      )}

      {layout &&
        fingerings.map((labels, index) =>
          labels && Number.isFinite(layout.noteXs[index]) ? (
            <div
              key={index}
              className={Styles.fingering}
              style={{ left: layout.noteXs[index], top: layout.pxHeight }}
              aria-label={`Correct fingering ${labels.join(' ')}`}
            >
              {labels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          ) : null,
        )}
    </div>
  );
};

export default ScaleNotes;